import React, { useEffect, useRef } from "react";
import CreatePostCard from "./CreatePost";
import JournalCard from "./Card";
import { usePostViewTracking } from "../../hooks/usePostViewTracking";
import { useBatchTrackViews } from "../../Controllers/Tribe/TribePostController";

const PostsSection = ({
  isAdminOnly,
  isPost,
  posts,
  groupDetails,
  onPostCreated,
  userProfile,
  handleScroll,
  hasMore,
  isLoading,
  onLoadMore,
  scrollRootRef,
  onPostSaved,
  isCurrentUserAdmin,
  onPostHide,
  showCreateTask,
  members,
  onPostUpdated,
  selectedPostId,
  onUserClicked,
  showPostOwnerInfo,
  postOwnerInfo,
  onPost,
  // selectedCommentId,
  // openCommentBox
  onGroupClicked,
  fallbackGroupId,
}) => {
  // Infinite scroll sentinel (more reliable than onScroll across different layouts)
  const loadMoreSentinelRef = useRef(null);
  const isFetchingMoreRef = useRef(false);
  const postOnMultipleGroup = isPost || false;

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    if (!loadMoreSentinelRef.current) return;

    const rootEl = scrollRootRef?.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (isLoading) return;
        if (isFetchingMoreRef.current) return;
        isFetchingMoreRef.current = true;
        Promise.resolve(onLoadMore()).finally(() => {
          // Small delay to avoid rapid-fire when sentinel stays in view
          setTimeout(() => {
            isFetchingMoreRef.current = false;
          }, 250);
        });
      },
      {
        root: rootEl,
        rootMargin: "200px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoading, scrollRootRef]);

  // View tracking setup
  const { mutateAsync: batchTrackViews } = useBatchTrackViews();
  const {
    viewCounts,
    startTracking,
    stopTracking,
    updateViewCount,
    flushBatch,
  } = usePostViewTracking(async (payload) => {
    const response = await batchTrackViews(payload);
    return response;
  });

  // Flush views when component unmounts (user navigates away from posts tab or tribe section)
  useEffect(() => {
    return () => {
      // Flush any pending views when leaving posts section
      if (flushBatch) {
        flushBatch();
      }
    };
  }, [flushBatch]);

  // Post refs for tracking (stores element and post data for comparison)
  const postRefs = useRef(new Map()); // postId -> { element, isViewedByCurrentUser, viewCount }

  // Initialize view counts from posts and update when posts change
  useEffect(() => {
    posts.forEach((post) => {
      // Update view count from API
      if (post.views !== undefined && post.views !== null) {
        updateViewCount(post.id, post.views);
      }
    });
  }, [posts, updateViewCount]);

  // Start/restart tracking posts when DOM is ready or posts change
  useEffect(() => {
    // Small delay to ensure DOM elements are rendered
    const timeoutId = setTimeout(() => {
      const currentPostIds = new Set(posts.map((p) => p.id));

      // Stop tracking posts that are no longer in the list
      postRefs.current.forEach((refData, postId) => {
        if (!currentPostIds.has(postId)) {
          stopTracking(postId);
          postRefs.current.delete(postId);
        }
      });

      // Start/restart tracking for all current posts
      posts.forEach((post) => {
        const element = document.getElementById(`post-${post.id}`);
        if (!element) return;

        const initialCount = post.views || 0;
        const isViewedByCurrentUser = post.is_viewed_by_current_user || false;
        const existingRef = postRefs.current.get(post.id);

        // Check if we need to restart tracking:
        // 1. Post not tracked yet
        // 2. Element changed (re-rendered)
        // 3. is_viewed_by_current_user status changed (important for re-tracking)
        // 4. View count changed significantly (might indicate post was updated)
        const needsRestart =
          !existingRef ||
          existingRef.element !== element ||
          existingRef.isViewedByCurrentUser !== isViewedByCurrentUser ||
          (existingRef.viewCount !== initialCount &&
            Math.abs(existingRef.viewCount - initialCount) > 1);

        if (needsRestart) {
          // Stop existing tracking if any
          if (existingRef) {
            stopTracking(post.id);
          }

          // Start new tracking
          startTracking(post.id, element, initialCount, isViewedByCurrentUser);
          postRefs.current.set(post.id, {
            element,
            isViewedByCurrentUser,
            viewCount: initialCount,
          });
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [posts, startTracking, stopTracking]);

  return (
    <>
      {(!isAdminOnly ||
        (isAdminOnly && isCurrentUserAdmin) ||
        (isAdminOnly && userProfile?.is_coach)) &&
        showCreateTask && (
          <CreatePostCard
            groupId={groupDetails?.about?.id}
            onPostCreated={onPostCreated}
            userProfile={userProfile}
            members={members}
            postOnMultipleGroup={postOnMultipleGroup}
          />
        )}

      {/* Display Post Owner Info when showPostOwnerInfo is true */}

      {posts.length > 0 ? (
        <>
          {posts.map((post) => {
            const viewCount = viewCounts.get(post.id) ?? post.views ?? 0;
            return (
              <div
                key={post.id}
                id={`post-${post.id}`} //ADD: This ID allows scrolling to specific posts
              >
                <JournalCard
                  post={post}
                  isAdminOnly={isAdminOnly}
                  onPostSaved={onPostSaved}
                  onPostHide={onPostHide}
                  userProfile={userProfile}
                  groupId={groupDetails?.about?.id ?? fallbackGroupId}
                  members={members}
                  onPostUpdated={onPostUpdated}
                  selectedPostId={selectedPostId}
                  viewCount={viewCount}
                  onUserClicked={onUserClicked}
                  onPost={onPost}
                  isPost={isPost}
                  onGroupClicked={onGroupClicked}
                  // selectedCommentId={selectedCommentId}
                  // openCommentBox={openCommentBox}
                />
              </div>
            );
          })}
          {/* {hasMore && (
            <button
              disabled={isLoading}
              style={{
                display: "block",
                margin: "20px auto",
                padding: "10px 20px",
                backgroundColor: "#008CFF",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              {isLoading ? "Loading..." : "Load More"}
            </button>
          )} */}
          {isLoading && (
            <div className="text-center py-2">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
        </>
      ) : (
        <div style={{ color: "#fff", padding: "20px", textAlign: "center" }}>
          No posts available
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      {hasMore && (
        <div
          ref={loadMoreSentinelRef}
          style={{ height: "1px", width: "100%" }}
        />
      )}
    </>
  );
};

export default PostsSection;
